/**
 * useTranscriptAnnouncer
 *
 * Off-screen polite live-region pattern for streaming transcript text.
 *
 * The naive approach — putting `aria-live="polite"` on the visible transcript
 * tree — causes screen readers (especially VoiceOver, NVDA in some modes) to
 * re-announce the entire paragraph (or region) on every keystroke from the
 * writer, because the segments/spans re-render in place. The fix is to keep
 * the visible region passive and route ONLY the newly-appended text delta to
 * a separate, visually-hidden live region.
 *
 * Returns a ref to attach to a sr-only div + the static JSX:
 *   <div ref={ref} className="sr-only" aria-live="polite" aria-atomic="false"
 *        aria-relevant="additions" />
 *
 * Internals:
 *   - flattens the document to a single string (paragraphs joined by '\n')
 *   - on each change, computes the appended delta vs. the previous snapshot
 *     (or the suffix after divergence point on rare mid-text edits)
 *   - flushes PHRASES, not words: at a sentence or line end, when the writer
 *     pauses, or at the next word gap once text has waited MAX_LATENCY_MS;
 *     FORCE_FLUSH_MS caps the wait for a word/URL with no gap. Per-keystroke
 *     flushing read letter by letter; per-word flushing (until 24. 9. 2026)
 *     queued one tiny announcement per word, which VoiceOver drops and any
 *     other speech (a volume change) cancels for good.
 *   - APPENDS each chunk as its own element and keeps the last few, instead
 *     of replacing the region's text: a replaced region only ever held the
 *     latest word, so an interrupted announcement was lost (retest 24. 9.
 *     2026: "comments on the volume change and never returns to the text")
 *   - when `isWriterTyping` is provided and transitions to false, immediately
 *     flushes the remaining buffer (typing pause = natural announcement break)
 *   - writes via DOM `textContent` rather than React state — React reconciler
 *     can elide same-string updates that we still want SRs to announce
 *   - skips the very first document (initial load would dump the whole
 *     transcript on a SR user landing on the page) — only that one: a viewer
 *     that joined before any text existed still hears the first words
 */

import { useCallback, useEffect, useRef } from 'react'
import type { DocumentState } from '@polygraf/shared'

/** A phrase ends here: sentence punctuation or a new line. */
const PHRASE_END_RE = /[.!?…\n]\s*$/
/** A word gap: somewhere a long-waiting phrase may be cut. */
const WORD_GAP_RE = /[\s,;:—–]$/
const PHRASE_FLUSH_MS = 300
/** Text waiting this long goes out at the next word gap, sentence or not. */
const MAX_LATENCY_MS = 2000
/** Absolute cap, for a run of text with no gap at all (a URL). */
const FORCE_FLUSH_MS = 4000
/** Announced chunks kept in the region; older ones are removed. */
const KEEP_CHUNKS = 5
/**
 * The first document a viewer receives is either history (a snapshot of a
 * transcript already running — must stay silent, or the whole lecture is read
 * out at once) or the writer's very first words after the viewer waited (must
 * be heard). The hook cannot see which frame kind it was, so it goes by size:
 * a first frame is a few words, a snapshot of a running lecture is long.
 */
const FIRST_FRAME_MAX_CHARS = 120

function flattenDocument(doc: DocumentState): string {
	return doc.paragraphs.map((p) => p.segments.map((s) => s.text).join('')).join('\n')
}

function computeDelta(prev: string, next: string): string {
	if (next.startsWith(prev)) return next.slice(prev.length)
	let i = 0
	while (i < prev.length && i < next.length && prev[i] === next[i]) i++
	return next.slice(i)
}

export function useTranscriptAnnouncer(
	document: DocumentState | null | undefined,
	isWriterTyping?: boolean,
) {
	const announcerRef = useRef<HTMLDivElement>(null)
	const prevTextRef = useRef('')
	const seededRef = useRef(false)
	const pendingDeltaRef = useRef('')
	const pendingSinceRef = useRef(0)
	const flushTimerRef = useRef<number | null>(null)
	const forceTimerRef = useRef<number | null>(null)

	const clearTimers = useCallback(() => {
		if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current)
		if (forceTimerRef.current !== null) window.clearTimeout(forceTimerRef.current)
		flushTimerRef.current = null
		forceTimerRef.current = null
	}, [])

	const flush = useCallback(() => {
		clearTimers()
		const text = pendingDeltaRef.current.trim()
		pendingDeltaRef.current = ''
		if (!text) return
		const el = announcerRef.current
		if (!el) return
		// `document` is the transcript prop in this scope; use the node's own.
		const chunk = el.ownerDocument.createElement('p')
		chunk.textContent = text
		el.appendChild(chunk)
		while (el.childNodes.length > KEEP_CHUNKS) el.firstChild?.remove()
	}, [clearTimers])

	useEffect(() => {
		if (!document) {
			// Source switched or connection reset: the next document starts over,
			// and pending text belongs to the old one.
			prevTextRef.current = ''
			seededRef.current = false
			pendingDeltaRef.current = ''
			clearTimers()
			return
		}

		const fullText = flattenDocument(document)
		const prev = prevTextRef.current
		prevTextRef.current = fullText

		if (!seededRef.current) {
			seededRef.current = true
			// History stays silent; a short first frame is the writer's first words.
			if (fullText.length > FIRST_FRAME_MAX_CHARS) return
		}
		if (fullText === prev) return

		// A pure deletion at the end (a typo corrected with Backspace): take the
		// removed characters back out of the text still waiting to be announced,
		// or "helo" + Backspace + "lo" is read as "helolo".
		if (prev.startsWith(fullText)) {
			const removed = prev.length - fullText.length
			pendingDeltaRef.current = pendingDeltaRef.current.slice(
				0,
				Math.max(0, pendingDeltaRef.current.length - removed),
			)
			return
		}

		const delta = computeDelta(prev, fullText)
		if (!delta) return

		const now = Date.now()
		if (!pendingDeltaRef.current) pendingSinceRef.current = now
		pendingDeltaRef.current += delta

		if (flushTimerRef.current !== null) {
			window.clearTimeout(flushTimerRef.current)
			flushTimerRef.current = null
		}
		const pending = pendingDeltaRef.current
		if (PHRASE_END_RE.test(pending)) {
			flushTimerRef.current = window.setTimeout(flush, PHRASE_FLUSH_MS)
		} else if (WORD_GAP_RE.test(pending) && now - pendingSinceRef.current >= MAX_LATENCY_MS) {
			flushTimerRef.current = window.setTimeout(flush, 50)
		}
		// The cap is armed once per chunk and never pushed back by more typing.
		if (forceTimerRef.current === null) {
			forceTimerRef.current = window.setTimeout(flush, FORCE_FLUSH_MS)
		}
	}, [document, flush, clearTimers])

	// Writer paused — a natural end of phrase.
	useEffect(() => {
		if (isWriterTyping === false) flush()
	}, [isWriterTyping, flush])

	useEffect(() => clearTimers, [clearTimers])

	return announcerRef
}
